import {
  HttpErrorResponse,
  HttpHandler,
  HttpInterceptor,
  HttpParams,
  HttpRequest,
} from '@angular/common/http';
import { IWAFConfig, WafEncoderUtility } from '../../utils/wafEncoderUtil';

import { API_HEADER_KEYS } from '../../constants/global.constant';
import { AnalyticsService } from '../../services/analytics-wrapper/analytics.service';
import { AppConfigService } from '../../services/app-config.service/app-config.service';
import { CommonUtility } from '../../utils/commonUtils';
import { HTTP_STATUS_CODES } from '../../constants/http-status-constant';
import { Injectable } from '@angular/core';
import { LoginService } from '../../services/login-service/login.service';
import { MaintenanceService } from '../../services/maintenance/maintenance.service';
import { ROUTER_CONSTANTS } from '../../constants/router.constants';
import { Router } from '@angular/router';
import { tap } from 'rxjs';
import { LocaleService } from '../../services/locale-wrapper/locale.service';
import { endPoints } from '../../constants/endpoints';

@Injectable()
export class RequestInterceptor implements HttpInterceptor {
  wafEncoderConfig: IWAFConfig = {
    restrictedCharsList: [],
    restrictedUnicodesList: [],
    isSessionConfigUpdated: false,
  };

  constructor(
    private readonly analyticsService: AnalyticsService,
    private readonly router: Router,
    private readonly loginService: LoginService,
    private readonly localeService: LocaleService,
    private appConfigService: AppConfigService,
    private readonly maintenanceService: MaintenanceService,
  ) {
    // Initialise configs with default values since session is not set yet
    WafEncoderUtility.setWAFEncoderConfig(
      this.wafEncoderConfig,
      this.appConfigService?.appConfig?.wafRestrictedUnicodes,
    );
  }

  intercept(request: HttpRequest<unknown>, next: HttpHandler) {
    // update WAF config if session was not updated earlier
    // once set use existing values
    if (!this.wafEncoderConfig.isSessionConfigUpdated) {
      WafEncoderUtility.setWAFEncoderConfig(
        this.wafEncoderConfig,
        this.appConfigService?.appConfig?.wafRestrictedUnicodes,
      );
    }

    const req = request.clone({
      url: request.url,
      setParams: this.encodeQueryParams(request.params),
      setHeaders: this.setHeaders(request),
      body: this.encodeRequestBody(request.body),
    });
    return next.handle(req).pipe(
      tap(
        () => {
          //Intentionally omitted because we are concerned with errors only
        },
        (error) => {
          this.handleError(error, request);
        },
      ),
    );
  }

  /**
   * Error handler
   * @param error error
   * @param request request
   */
  handleError(error: any, request: any) {
    if (error instanceof HttpErrorResponse) {
      if (error.status === HTTP_STATUS_CODES.UNAUTHORIZED) {
        if (
          !error.url?.includes(endPoints.casemgmt.login.URI) &&
          !error.url?.includes(endPoints.casemgmt.syncAccountContact.URI) &&
          !error.url?.includes(endPoints.casemgmt.upsContactOnboarding.URI) &&
          this.loginService.beginAuthRecovery()
        ) {
          this.router.navigate([ROUTER_CONSTANTS.login], {
            queryParams: {
              returnto: CommonUtility.getLoginReturnTo(
                CommonUtility.getQueryParams().returnto,
              ),
            },
          });
        }
      } else if (error.status === HTTP_STATUS_CODES.UNAUTHENTICATED) {
        this.loginService.hasAccess = false;
        this.router.navigate([ROUTER_CONSTANTS.noAccess]);
      } else if (error.status === HTTP_STATUS_CODES.SERVICE_UNAVAILABLE) {
        // AC-4: redirect immediately instead of waiting for the next status poll
        this.maintenanceService.activateImmediately();
      }
      const url = new URL(CommonUtility.sanitizeUrl(error.url ?? ''));
      const errorEventDesc = CommonUtility.getApiAnalyticsErrorDesc(
        url.pathname,
      );
      if (
        url.pathname.startsWith('/api') &&
        request.method === 'GET' &&
        errorEventDesc
      ) {
        // Track only api failures
        this.analyticsService.createLinkClickTagObject(
          '#',
          'Load Error',
          errorEventDesc,
          'Internal',
        );
      }
    }
  }

  /**
   * setHeaders
   * @param request request
   * @returns headers
   */
  setHeaders(request: HttpRequest<unknown>): any {
    const headers: any = request.headers.keys().reduce((res, val) => {
      (res as any)[val] = request.headers.get(val);
      return res;
    }, {});
    let xsrfToken = '';
    const allCookies = document.cookie;
    const allCookiesSplit = allCookies.split(';');
    const xsrf_cookie = allCookiesSplit.find(
      (ele) => ele.indexOf(API_HEADER_KEYS.X_SCS_XSRF_TOKEN) >= 0,
    );
    if (xsrf_cookie) {
      xsrfToken = xsrf_cookie.split('=')[1];
    }
    if (request.url.indexOf('googleapis') === -1) {
      if (xsrfToken) {
        headers[API_HEADER_KEYS.X_XSRF_TOKEN] = xsrfToken;
      }
    }
    const locale = this.localeService.getUrlOrSessionLocale();
    headers.locale = locale;
    return headers;
  }

  /**
   * Converts Special Characters In QueryParams
   * @param params URL paramteres
   * @returns Encoded QueryParams
   */
  encodeQueryParams(params: HttpParams): Record<string, string> {
    const queryParams: Record<string, string> = {};

    params.keys().forEach((key: string) => {
      // Though return type of get() is string, its still returning non-string as well
      const value: any = params.get(key);
      queryParams[key] = value;

      // Only encode when type is string, since we are iterating char by char in convertToUnicode
      if (typeof value === 'string') {
        const convertedValue: string = WafEncoderUtility.convertToUnicode(
          value,
          this.wafEncoderConfig.restrictedUnicodesList,
          this.wafEncoderConfig.restrictedCharsList,
        );
        queryParams[key] = convertedValue;
      }
    });
    return queryParams;
  }

  /**
   * Converts charcters to unicode in request body
   * @param reqBody Request Body
   * @returns Converted request body
   */
  encodeRequestBody(reqBody: any): any {
    if (reqBody) {
      // For formData stringify gives {} so encoding explicitly
      if (reqBody instanceof FormData) {
        this.encodeFormData(reqBody);
      } else {
        const stringifiedRequest: string = JSON.stringify(reqBody);

        return JSON.parse(stringifiedRequest, (_, value) =>
          typeof value === 'string'
            ? WafEncoderUtility.convertToUnicode(
                value,
                this.wafEncoderConfig.restrictedUnicodesList,
                this.wafEncoderConfig.restrictedCharsList,
              )
            : value,
        );
      }
    }

    return reqBody;
  }

  /**
   * Encode special characters in Request body when type is formdata
   * @param reqBody Request  body
   */
  encodeFormData(reqBody: FormData): void {
    reqBody.forEach((value: FormDataEntryValue, key: string) => {
      if (typeof value === 'string') {
        const unicodedValue: string = WafEncoderUtility.convertToUnicode(
          value,
          this.wafEncoderConfig.restrictedUnicodesList,
          this.wafEncoderConfig.restrictedCharsList,
        );
        reqBody.set(key, unicodedValue);
      }
    });
  }
}
